<script lang="ts" setup>
import { ShoppingCartIcon } from "@heroicons/vue/24/outline";
import { onClickOutside } from "@vueuse/core";

const commerceStore = useCommerceStore();
const { totalItemsInCart, showCart, cart } = storeToRefs(commerceStore);

const router = useRouter();
const target = ref(null);

const goTo = (path: string) => {
  router.push(path);
};

const closeMiniCart = () => {
  commerceStore.toggleMiniCart({ open: false });
};
const openMiniCart = () => {
  commerceStore.toggleMiniCart({ open: true });
};

onClickOutside(target, () => {
  closeMiniCart();
});
const mainStore = useMainStore();

const globalSettings = computed(() => mainStore?.globalSettings);
const { currency, getProductPrice, getProductDiscountValue } = formatterUtils();

// Helper function to reliably get price for all products including affiliate products
const getReliablePrice = (product: any, withDiscount: boolean = true): number => {
  // Get the recurring prices array
  const recurringPrices = product?.productOfferingPrice?.recurring || [];
  
  // If we have recurring prices, use them
  if (recurringPrices.length > 0) {
    // Get the first recurring price (most products only have one)
    const priceObject = recurringPrices[0];
    
    // If we want to apply discount and it exists
    if (withDiscount && priceObject?.price?.taxIncludedAmountAfterDiscount) {
      return parseFloat(priceObject.price.taxIncludedAmountAfterDiscount) || 0;
    }
    
    // Otherwise use regular price
    if (priceObject?.price?.taxIncludedAmount) {
      return parseFloat(priceObject.price.taxIncludedAmount) || 0;
    }
  }
  
  // If no recurring prices, try oneTime prices
  const oneTimePrices = product?.productOfferingPrice?.oneTime || [];
  if (oneTimePrices.length > 0 && oneTimePrices[0]?.price?.taxIncludedAmount) {
    return parseFloat(oneTimePrices[0].price.taxIncludedAmount) || 0;
  }
  
  // Last resort: try standard method (for backward compatibility)
  const standardPrice = getProductPrice(product, withDiscount);
  if (!isNaN(standardPrice) && standardPrice !== undefined) {
    return standardPrice;
  }
  
  // Ultimate fallback, return 0
  return 0;
};

// Helper function to reliably get discount amount for all products
const getReliableDiscount = (product: any): number => {
  // Get the recurring prices array
  const recurringPrices = product?.productOfferingPrice?.recurring || [];
  
  // If we have recurring prices, use the discount amount from the first one
  if (recurringPrices.length > 0 && recurringPrices[0]?.price?.discountAmount) {
    return parseFloat(recurringPrices[0].price.discountAmount) || 0;
  }
  
  // Fallback to oneTime prices if available
  const oneTimePrices = product?.productOfferingPrice?.oneTime || [];
  if (oneTimePrices.length > 0 && oneTimePrices[0]?.price?.discountAmount) {
    return parseFloat(oneTimePrices[0].price.discountAmount) || 0;
  }
  
  // Last resort: try standard method
  const standardDiscount = getProductDiscountValue(product);
  if (!isNaN(standardDiscount) && standardDiscount !== undefined) {
    return standardDiscount;
  }
  
  // Ultimate fallback, return 0
  return 0;
};

const removeItem = (productCode: string) => {
  commerceStore.removeProduct(productCode);
};
</script>
<template>
  <div ref="target" class="mini-cart">
    <button
      class="flex items-center gap-2.5"
      @click="goTo('/cart')"
      @mouseenter="openMiniCart"
    >
      <!-- OnePass themed cart icon (white) -->
      <img
        class="h-14 w-14 cart-icon-white"
        src="/icons/cart-icon-onepass.svg"
        alt="OnePass Cart"
      />
      <div v-if="totalItemsInCart > 0" class="cart-count">
        {{ totalItemsInCart }}
      </div>
    </button>
    <Transition name="slide-from-top">
      <div v-if="showCart" class="cart-content shadow-2xl" @mouseleave="closeMiniCart">
        <div class="flex flex-col w-full bg-white rounded-md overflow-hidden">
          <div class="text-lg font-semibold bg-primary text-white px-4 py-3">
            My Cart ({{ totalItemsInCart }})
          </div>
          <!-- ORDER ITEMS -->
          <div class="max-h-[350px] overflow-y-auto">
            <div
              v-for="({ product, ...item }, index) in cart.orderItems"
              :key="index"
              class="cart-item border-b border-gray-200 last:border-b-0"
            >
              <div class="flex p-4 gap-4 items-center">
                <!-- IMAGE -->
                <div class="cart-image-container">
                  <NuxtImg
                    v-if="globalSettings?.imgCart?.url"
                    loading="lazy"
                    provider="strapi"
                    :src="globalSettings.imgCart.url"
                    :alt="product.name"
                    class="w-16 h-16 object-contain"
                  />
                  <div
                    v-else
                    class="w-16 h-16 bg-gray-200 rounded flex items-center justify-center"
                  >
                    <span class="text-gray-400 text-xs">No Image</span>
                  </div>
                </div>
                <!-- DETAILS -->
                <div class="flex-1 min-w-0">
                  <div class="flex justify-between items-start gap-2 mb-1">
                    <h3 class="font-medium text-sm text-gray-800 line-clamp-2">{{ product.name }}</h3>
                    <button 
                      class="text-gray-400 hover:text-red-500 p-1"
                      @click="removeItem(product.code)"
                      aria-label="Remove item"
                    >
                      <span class="text-lg">×</span>
                    </button>
                  </div>
                  <div class="flex flex-col text-sm">
                    <div class="text-gray-600">
                      {{ item.quantity }} × {{ currency(getReliablePrice(product)) }}
                    </div>
                    <div class="font-semibold text-black mt-1">
                      <span
                        v-if="getReliableDiscount(product) > 0"
                        class="line-through text-gray-500 text-xs mr-2"
                      >
                        {{ currency(getReliablePrice(product, false) * item.quantity) }}
                      </span>
                      {{ currency(getReliablePrice(product) * item.quantity) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- TOTAL -->
          <div class="bg-gray-100 px-4 py-3 border-t border-gray-200">
            <div class="flex justify-between items-center font-medium text-base">
              <div>Total</div>
              <div class="text-primary font-bold">
                {{ currency(cart.total) }}
              </div>
            </div>
          </div>
        </div>
        <!-- Buttons -->
        <div class="flex gap-3 p-4 bg-white border-t border-gray-200">
          <button
            class="flex-1 px-4 py-2.5 text-primary bg-white text-sm font-medium rounded border border-primary hover:bg-gray-50 transition-colors"
            @click="goTo('/cart')"
          >
            VIEW CART
          </button>
          <button
            class="flex-1 px-4 py-2.5 text-white bg-primary text-sm font-medium rounded hover:bg-primary-dark transition-colors"
            @click="goTo('/checkout')"
          >
            CHECKOUT
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped lang="scss">
.mini-cart {
  @apply flex items-center gap-2.5 relative;
  
  .cart-content {
    @apply flex flex-col w-[380px] absolute right-0 top-12 z-50 filter drop-shadow-xl;
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1);
  }
  
  .cart-item {
    @apply w-full;
    
    &:hover {
      @apply bg-gray-50;
    }
  }
  
  .cart-image-container {
    @apply flex-shrink-0;
  }
}

.cart-count {
  @apply absolute -top-2 -right-2 text-white text-xs bg-[#9900F1] rounded-full w-5 h-5 flex justify-center items-center font-medium;
}

/* Make cart icon white */
.cart-icon-white {
  filter: brightness(0) invert(1); /* This turns the icon white */
}

/* Animation for sliding the cart from top */
.slide-from-top-enter-active,
.slide-from-top-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-from-top-enter-from,
.slide-from-top-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}
</style>
